import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import Layout from "../components/Layout";
import SafeHtml from "../components/SafeHtml";
import { toast } from "sonner";
import PageAdvice from "../components/PageAdvice";
import {
  ArrowLeft, CircleNotch, WarningCircle, ClipboardText, MapPin, Phone, User,
  CurrencyDollar, Receipt, FileText, Note, Paperclip, ShoppingCart, Wrench,
  CheckSquare, Buildings, CalendarBlank, Hash, Users, Flag, FolderSimple,
  PushPin, DownloadSimple, ArrowsClockwise, Database, Gauge,
} from "@phosphor-icons/react";

const fmtSync = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "never");

const STATUS_COLORS = {
  NOT_STARTED: "#6B7280", IN_PROGRESS: "#0055FF", COMPLETED: "#059669",
  ARCHIVED: "#9CA3AF", INVOICED: "#7C3AED", CANCELLED: "#DC2626",
};
const sc = (s) => STATUS_COLORS[(s || "").toUpperCase()] || "#6B7280";
const money = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fileSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

// Aroflo returns postedBy as either a string or a user object — render a safe name string.
function postedByName(p) {
  if (!p) return "";
  if (typeof p === "string") return p;
  return [p.givenName, p.familyName].filter(Boolean).join(" ") || p.name || p.position || p.email || "";
}

function conditionTone(value) {
  const n = parseInt(value, 10);
  if (!n) return "bg-neutral-100 text-neutral-500";
  if (n >= 4) return "bg-[#DC2626]/10 text-[#DC2626]";
  if (n === 3) return "bg-[#D97706]/10 text-[#D97706]";
  return "bg-[#059669]/10 text-[#059669]";
}

function JobConditions({ jobId }) {
  const [data, setData] = useState(undefined);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${jobId}/conditions`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [jobId]);

  const refresh = () => {
    setRefreshing(true);
    toast.promise(
      api.get(`/jobs/${jobId}/conditions`, { params: { refresh: true } })
        .then((r) => setData(r.data))
        .finally(() => setRefreshing(false)),
      { loading: "Pulling latest conditions from Aroflo…", success: "Conditions refreshed.", error: "Refresh failed." }
    );
  };

  const assets = data?.assets || [];
  return (
    <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm" data-testid="job-conditions">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight">
          <Gauge size={18} /> Asset Condition Report
          {assets.length > 0 && <span className="text-sm font-normal text-neutral-400">({assets.length})</span>}
        </h3>
        {data !== undefined && data !== null && (
          <div className="flex items-center gap-2.5">
            <span
              className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-[11px] font-semibold ${data._source === "live" ? "bg-[#059669]/10 text-[#059669]" : "bg-neutral-100 text-neutral-500"}`}
              data-testid="job-conditions-source"
              title={data._checked_at ? `Last checked ${fmtSync(data._checked_at)}` : ""}
            >
              <Database size={12} /> {data._source === "live" ? "Live" : "Cached"}{data._checked_at ? ` · ${fmtSync(data._checked_at)}` : ""}
            </span>
            <button
              onClick={refresh}
              disabled={refreshing}
              data-testid="job-conditions-refresh"
              className="flex items-center gap-1.5 rounded-sm border border-[#0055FF] px-2.5 py-1 text-xs font-semibold text-[#0055FF] transition-all duration-200 hover:bg-[#0055FF]/5 disabled:opacity-50"
            >
              <ArrowsClockwise size={13} weight="bold" className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        )}
      </div>
      {data === undefined ? (
        <div className="flex h-16 items-center justify-center"><CircleNotch size={20} className="animate-spin text-[#0055FF]" /></div>
      ) : assets.length === 0 ? (
        <p className="text-sm text-neutral-400" data-testid="job-conditions-empty">No assets attached to this job.</p>
      ) : (
        <div className="space-y-4">
          {data.filled_values === 0 && (
            <p className="rounded-sm bg-[#D97706]/10 px-3 py-2 text-xs font-semibold text-[#D97706]" data-testid="job-conditions-unfilled">
              Condition ratings not yet completed by a technician on this job.
            </p>
          )}
          {assets.map((a, i) => (
            <div key={a.asset_id || `asset-${i}`} className="rounded-sm border border-neutral-200 p-3" data-testid={`job-condition-asset-${i}`}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <button
                  onClick={() => a.asset_id && window.open(`/asset-detail?id=${encodeURIComponent(a.asset_id)}`, "_blank", "noopener")}
                  className="text-sm font-bold text-[#0055FF] hover:underline"
                  data-testid={`job-condition-asset-link-${i}`}>
                  {a.asset_name || "Asset"}
                </button>
                <span className="text-xs text-neutral-500">
                  {a.checked_by ? `Checked by ${a.checked_by}` : "Not checked"}
                  {a.datetime_checked ? ` · ${String(a.datetime_checked).replace("T", " ").slice(0, 16)}` : ""}
                </span>
              </div>
              {a.fields.length === 0 ? (
                <p className="text-xs text-neutral-400">No condition fields on this asset.</p>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {a.fields.map((f, fi) => (
                    <div key={f.name || `field-${fi}`} className="flex items-center justify-between gap-2 rounded-sm bg-neutral-50 px-2.5 py-1.5">
                      <span className="text-xs text-neutral-500" title={f.name}>{(f.name || "").split("(")[0].trim()}</span>
                      <span className={`inline-flex min-w-[26px] justify-center rounded-sm px-2 py-0.5 text-sm font-bold ${conditionTone(f.value)}`}>
                        {f.value != null && f.value !== "" ? f.value : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
      <div className="flex items-center gap-2 text-neutral-500"><Icon size={16} weight="bold" /><span className="text-xs uppercase tracking-widest font-semibold">{label}</span></div>
      <p className={`mt-2 font-heading text-2xl font-black tracking-tighter ${accent || "text-[#0A0A0A]"}`}>{value}</p>
    </div>
  );
}

function Row({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-4 border-b border-neutral-100 py-2.5 last:border-0">
      <span className="text-sm text-neutral-500">{label}</span>
      <span className="text-sm font-semibold text-[#0A0A0A] text-right">{value}</span>
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 border border-neutral-200 bg-neutral-50 px-3 py-2.5 rounded-sm">
      <Icon size={18} className="text-neutral-400" />
      <div><p className="text-xs text-neutral-500">{label}</p><p className="text-sm font-bold text-[#0A0A0A]">{value}</p></div>
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/jobs/${id}`).then((r) => setD(r.data)).catch((e) => setError(e.response?.data?.detail || e.message)).finally(() => setLoading(false));
  }, [id]);

  const refresh = () => {
    setRefreshing(true);
    toast.promise(
      api.get(`/jobs/${id}`, { params: { refresh: true } }).then((r) => setD(r.data)).finally(() => setRefreshing(false)),
      { loading: "Pulling latest from Aroflo…", success: "Job refreshed from Aroflo.", error: "Refresh failed." }
    );
  };

  if (loading) return <Layout><div className="flex h-64 items-center justify-center" data-testid="job-loading"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;
  if (error) return <Layout><div className="flex h-64 flex-col items-center justify-center gap-2 text-neutral-600" data-testid="job-error"><WarningCircle size={28} className="text-[#D97706]" />{error}</div></Layout>;

  const c = d.counts || {};
  const loc = d.location || {};
  const client = d.client || {};
  const site = [loc.address1, loc.address2, loc.suburb, loc.state, loc.postCode].filter(Boolean).join(", ");
  const resources = d.resources || [];
  const notes = d.notes || [];
  const documents = d.documents || [];
  const resName = (res) => [res?.givenName, res?.familyName].filter(Boolean).join(" ") || res?.position || "Team member";

  const downloadPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/jobs/${id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `job-${d.jobNumber || id}-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF export failed", e?.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div data-testid="job-detail-page">
        <button data-testid="job-back" onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#0055FF]"><ArrowLeft size={16} weight="bold" /> Back</button>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#0A0A0A] text-white"><ClipboardText size={24} weight="fill" /></div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" data-testid="job-number">Job #{d.jobNumber}</h1>
              <p className="text-sm text-neutral-500">{d.taskType?.name || "Job"} · {client.id ? <button onClick={() => navigate(`/clients/${client.id}`)} className="font-semibold text-[#0055FF] hover:underline">{client.name}</button> : "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-sm px-3 py-1.5 text-sm font-semibold" style={{ background: sc(d.status) + "1a", color: sc(d.status) }} data-testid="job-status">{d.status}</span>
            <button
              data-testid="job-refresh-btn"
              onClick={refresh}
              disabled={refreshing}
              className="flex items-center gap-2 rounded-sm border border-[#0055FF] px-3.5 py-1.5 text-sm font-semibold text-[#0055FF] transition-all duration-200 hover:bg-[#0055FF]/5 disabled:opacity-50"
            >
              <ArrowsClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="job-export-pdf"
              onClick={downloadPdf}
              disabled={exporting}
              className="flex items-center gap-2 rounded-sm bg-[#0A0A0A] px-4 py-1.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0055FF] disabled:opacity-50"
            >
              {exporting ? <CircleNotch size={16} className="animate-spin" /> : <DownloadSimple size={16} weight="bold" />} Download PDF
            </button>
          </div>
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-neutral-400" data-testid="job-synced-at">
          <Database size={12} /> {d._source === "db" ? "From cache · " : "Synced · "}{fmtSync(d._cached_at)}
        </p>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat icon={CurrencyDollar} label="Total Cost (ex)" value={money(c.totalCostEx)} />
          <Stat icon={CurrencyDollar} label="Total Sell (ex)" value={money(c.totalSellEx)} accent="text-[#0055FF]" />
          <Stat icon={Receipt} label="Margin" value={c.margin != null ? `${Number(c.margin).toFixed(1)}%` : "—"} accent="text-[#059669]" />
          <Stat icon={CheckSquare} label="Invoiced" value={d.invoiced ? "Yes" : "No"} accent={d.invoiced ? "text-[#059669]" : "text-[#D97706]"} />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6">
            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
              <h3 className="mb-2 font-heading text-lg font-black tracking-tight">Job Details</h3>
              <Row label="Reference" value={d.referenceNumber} />
              <Row label="Customer Order #" value={d.customerOrderNumber} />
              <Row label="Requested" value={d.requestedDateTime?.slice(0, 10)} />
              <Row label="Due Date" value={d.dueDate} />
              <Row label="Status" value={d.status} />
              <Row label="Priority" value={d.priority?.description} />
              <Row label="Project" value={d.project?.name ? `${d.project.name}${d.project.projectNumber ? ` (#${d.project.projectNumber})` : ""}` : null} />
              <Row label="Business Unit" value={d.owner?.name} />
              <Row label="Entered By" value={d.enteredBy} />
              <Row label="Reported By" value={d.reportedBy} />
              <Row label="Progress" value={d.progress != null ? `${Math.round(d.progress)}%` : null} />
            </div>

            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm" data-testid="job-team">
              <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-black tracking-tight"><Users size={18} /> Assigned Team</h3>
              {resources.length === 0 ? (
                <p className="text-sm text-neutral-400">No team members assigned.</p>
              ) : (
                <div className="space-y-2.5">
                  {resources.map((r) => {
                    const res = r.resource || {};
                    return (
                      <button key={r.id} onClick={() => res.id && navigate(`/technicians/${res.id}`)} className="flex w-full items-center gap-3 rounded-sm text-left transition-colors duration-150 hover:bg-neutral-50" data-testid={`job-resource-${r.id}`}>
                        {res.avatar ? (
                          <img src={res.avatar} alt="" className="h-9 w-9 rounded-sm object-cover" />
                        ) : (
                          <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#0A0A0A] text-xs font-bold text-white">{resName(res).split(" ").map((x) => x[0]).join("").slice(0, 2)}</div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold text-[#0055FF] hover:underline">{resName(res)}</p>
                          <p className="truncate text-xs text-neutral-500">{[res.position, res.email].filter(Boolean).join(" · ") || "—"}</p>
                        </div>
                        <span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-600">{r.status}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
              <h3 className="mb-2 flex items-center gap-2 font-heading text-lg font-black tracking-tight"><MapPin size={18} /> Site</h3>
              <Row label="Address" value={site || d.locationText} />
              <Row label="Site Contact" value={loc.siteContact} />
              <Row label="Site Phone" value={loc.sitePhone} />
              {(site || d.locationText) && (
                <a
                  href={`https://www.google.com/maps?q=${encodeURIComponent(site || d.locationText)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative mt-3 block overflow-hidden rounded-sm border border-neutral-200"
                  data-testid="job-map-link"
                >
                  <iframe
                    title="Job site map"
                    className="pointer-events-none h-48 w-full"
                    loading="lazy"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(site || d.locationText)}&output=embed`}
                  />
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-sm bg-[#0A0A0A]/85 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm transition-all duration-200 group-hover:bg-[#0055FF]">
                    <MapPin size={14} weight="fill" /> Open in Google Maps
                  </span>
                </a>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
              <h3 className="mb-3 font-heading text-lg font-black tracking-tight">Description</h3>
              {d.description ? (
                <SafeHtml html={d.description} className="prose prose-sm max-w-none text-sm text-neutral-700" data-testid="job-description" />
              ) : <p className="text-sm text-neutral-400">No description.</p>}
              <p className="mt-3 text-sm font-semibold text-[#0A0A0A]">{d.name}</p>
            </div>

            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
              <h3 className="mb-3 font-heading text-lg font-black tracking-tight">Activity & Records</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Mini icon={FileText} label="Forms" value={`${c.completedForms || 0}/${c.totalForms || 0}`} />
                <Mini icon={CheckSquare} label="Checklist" value={`${c.completedChecklistItems || 0}/${c.totalChecklistItems || 0}`} />
                <Mini icon={Note} label="Notes" value={c.taskNotes || 0} />
                <Mini icon={Paperclip} label="Documents" value={c.documents || 0} />
                <Mini icon={ShoppingCart} label="Purchase Orders" value={c.purchaseOrders || 0} />
                <Mini icon={Receipt} label="Invoices" value={c.invoices || 0} />
                <Mini icon={Wrench} label="Labour (hrs)" value={c.labour || 0} />
                <Mini icon={CurrencyDollar} label="Materials" value={money(c.materials)} />
                <Mini icon={Hash} label="Uninvoiced Items" value={c.uninvoicedItemsCount || 0} />
                <Mini icon={FileText} label="Field Forms" value={`${c.completedFieldForms || 0}/${c.totalFieldForms || 0}`} />
                <Mini icon={ClipboardText} label="Work Orders" value={c.workOrders || 0} />
                <Mini icon={CurrencyDollar} label="Expenses" value={money(c.expenses)} />
              </div>
            </div>

            {/* Asset Condition Report */}
            <JobConditions jobId={id} />

            {/* Notes */}
            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm" data-testid="job-notes">
              <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-black tracking-tight"><Note size={18} /> Notes <span className="text-sm font-normal text-neutral-400">({notes.length})</span></h3>
              {notes.length === 0 ? (
                <p className="text-sm text-neutral-400">No notes recorded.</p>
              ) : (
                <div className="space-y-3">
                  {notes.map((n, i) => {
                    const note = n.note || n;
                    return (
                      <div key={note.id || `note-${i}`} className="rounded-sm border border-neutral-200 bg-neutral-50 p-3" data-testid={`job-note-${i}`}>
                        <div className="mb-1.5 flex flex-wrap items-center gap-2">
                          {note.sticky && <PushPin size={13} weight="fill" className="text-[#D97706]" />}
                          {note.visibility && <span className="rounded-sm bg-neutral-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-600">{String(note.visibility).replace(/_/g, " ")}</span>}
                          {note.postedBy && <span className="text-xs text-neutral-500">{postedByName(note.postedBy)}</span>}
                        </div>
                        {note.content ? (
                          <SafeHtml html={note.content} className="prose prose-sm max-w-none text-sm text-neutral-700" />
                        ) : <p className="text-sm text-neutral-400">—</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Documents */}
            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm" data-testid="job-documents">
              <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-black tracking-tight"><Paperclip size={18} /> Documents <span className="text-sm font-normal text-neutral-400">({documents.length})</span></h3>
              {documents.length === 0 ? (
                <p className="text-sm text-neutral-400">No documents attached.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {documents.map((doc, i) => {
                    const by = doc.insertedByUser ? [doc.insertedByUser.givenName, doc.insertedByUser.familyName].filter(Boolean).join(" ") : null;
                    const isImg = (doc.documentType || "").toLowerCase() === "img";
                    const url = `${API}/jobs/${id}/documents/${doc.id}/download`;
                    const meta = [doc.documentType?.toUpperCase(), fileSize(doc.fileSizeBytes), by].filter(Boolean).join(" · ");
                    const onClick = () => (isImg ? setLightbox({ url, name: doc.displayName || doc.fileName }) : window.open(url, "_blank", "noopener"));
                    return (
                      <button key={doc.id || `doc-${i}`} onClick={onClick} data-testid={`job-document-${i}`} title={doc.displayName || doc.fileName}
                        className="group flex flex-col overflow-hidden rounded-sm border border-neutral-200 bg-neutral-50 text-left transition-all duration-200 hover:border-[#0055FF] hover:shadow-md">
                        <div className="flex h-28 items-center justify-center overflow-hidden bg-neutral-100">
                          {isImg ? (
                            <img src={url} alt={doc.displayName || ""} loading="lazy" className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
                          ) : (
                            <FileText size={34} className="text-neutral-400" />
                          )}
                        </div>
                        <div className="p-2.5">
                          <p className="truncate text-sm font-semibold text-[#0A0A0A]">{doc.displayName || doc.name || "Document"}</p>
                          <p className="truncate text-xs text-neutral-500">{meta}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <PageAdvice items={[
          "This job is cached in the Hub database. Use \u201cRefresh\u201d to pull the latest status, team, notes and documents from Aroflo.",
          "\u201cDownload PDF\u201d always generates from live Aroflo data, regardless of the cache.",
          "Documents and images are streamed on demand from Aroflo when opened.",
        ]} />
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(null)} data-testid="document-lightbox">
          <div className="relative max-h-[92vh] max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between gap-4">
              <p className="truncate text-sm font-semibold text-white">{lightbox.name}</p>
              <div className="flex items-center gap-2">
                <a href={lightbox.url} target="_blank" rel="noreferrer" download className="flex items-center gap-1.5 rounded-sm bg-white/15 px-3 py-1.5 text-sm font-semibold text-white hover:bg-white/25" data-testid="lightbox-download"><DownloadSimple size={15} weight="bold" /> Open / Download</a>
                <button onClick={() => setLightbox(null)} data-testid="lightbox-close" className="rounded-sm bg-white/15 px-3 py-1.5 text-sm font-semibold text-white hover:bg-white/25">Close</button>
              </div>
            </div>
            <img src={lightbox.url} alt={lightbox.name} className="max-h-[82vh] max-w-full rounded-sm object-contain" />
          </div>
        </div>
      )}
    </Layout>
  );
}
